import { Box, Card, CardContent, CircularProgress, Skeleton, styled } from '@mui/material';
import { CSSProperties, ReactNode, forwardRef, memo, useCallback, useEffect, useRef } from 'react';
import { useNavigationType } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeGrid as Grid, GridOnScrollProps, GridOnItemsRenderedProps } from 'react-window';

const SCROLL_STORAGE_PREFIX = 'scroll-position:cards:';
const PAGE_COUNT_STORAGE_PREFIX = 'card-page-count:';
const SCROLL_SAVE_DEBOUNCE_MS = 150;

const MIN_CARD_WIDTH = 240;
const CARD_HEIGHT = 264;
const GAP = 16;
const PADDING = 16;
const ROW_HEIGHT = CARD_HEIGHT + GAP;
const OVERSCAN_ROW_COUNT = 1;
const PENDING_ROW_COUNT = 1;

const readSavedPosition = (storageKey: string): number => {
  const saved = Number(sessionStorage.getItem(storageKey));
  return Number.isFinite(saved) && saved > 0 ? saved : 0;
};

export const getRenderableCellCount = ({
  loadedCount,
  total,
  columnCount,
  hasNextPage,
}: {
  loadedCount: number;
  total: number;
  columnCount: number;
  hasNextPage: boolean;
}) => {
  if (!hasNextPage) return loadedCount;
  const pendingCells = Math.min(columnCount * PENDING_ROW_COUNT, total - loadedCount);
  return loadedCount + Math.max(0, pendingCells);
};

const GridInner = forwardRef<HTMLDivElement, { style: CSSProperties }>(({ style, ...rest }, reference) => (
  <div ref={reference} style={{ ...style, height: Number(style.height) + PADDING }} {...rest} />
));
GridInner.displayName = 'GridInner';

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 24,
});

const SkeletonCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.divider}`,
}));

const PlaceholderCard = () => (
  <SkeletonCard>
    <Skeleton variant='rectangular' height={180} animation='wave' />
    <CardContent sx={{ flexGrow: 1, padding: '12px', '&:last-child': { paddingBottom: '12px' } }}>
      <Skeleton width='70%' height={20} />
      <Skeleton width='40%' height={16} sx={{ mt: 0.5 }} />
    </CardContent>
  </SkeletonCard>
);

const CardGridInner = <T,>({
  items,
  total,
  renderCard,
  getItemKey,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  emptyState,
  scrollKey,
  pageCount,
  resetKey,
}: {
  items: T[];
  total: number;
  renderCard: (item: T) => ReactNode;
  getItemKey: (item: T) => string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void | Promise<unknown>;
  isLoading: boolean;
  emptyState?: ReactNode;
  scrollKey?: string;
  pageCount?: number;
  resetKey?: string;
}) => {
  const gridReference = useRef<Grid>(null);
  const navigationType = useNavigationType();
  const scrollTimerReference = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hasRestoredScroll = useRef(false);
  const previousScrollKey = useRef(scrollKey);
  const previousResetKey = useRef(resetKey);
  const columnCountReference = useRef(1);

  const latestReference = useRef({ items, hasNextPage, isFetchingNextPage, fetchNextPage, pageCount, scrollKey });
  latestReference.current = { items, hasNextPage, isFetchingNextPage, fetchNextPage, pageCount, scrollKey };

  useEffect(() => {
    if (previousResetKey.current === resetKey) return;
    previousResetKey.current = resetKey;
    gridReference.current?.scrollTo({ scrollTop: 0, scrollLeft: 0 });
  }, [resetKey]);

  const handleScroll = useCallback(({ scrollTop }: GridOnScrollProps) => {
    const { scrollKey, pageCount } = latestReference.current;
    if (!scrollKey) return;
    clearTimeout(scrollTimerReference.current);
    scrollTimerReference.current = setTimeout(() => {
      sessionStorage.setItem(`${SCROLL_STORAGE_PREFIX}${scrollKey}`, String(scrollTop));
      if (pageCount) {
        sessionStorage.setItem(`${PAGE_COUNT_STORAGE_PREFIX}${scrollKey}`, String(pageCount));
      }
    }, SCROLL_SAVE_DEBOUNCE_MS);
  }, []);

  const handleItemsRendered = useCallback(({ visibleRowStopIndex }: GridOnItemsRenderedProps) => {
    const { isFetchingNextPage, hasNextPage, fetchNextPage, items } = latestReference.current;
    if (isFetchingNextPage || !hasNextPage) return;
    const colCount = columnCountReference.current;
    if (!colCount) return;
    if (visibleRowStopIndex >= Math.ceil(items.length / colCount) - 2) {
      fetchNextPage();
    }
  }, []);

  useEffect(() => {
    if (previousScrollKey.current !== scrollKey) {
      previousScrollKey.current = scrollKey;
      hasRestoredScroll.current = false;
    }
    if (!scrollKey || navigationType !== 'POP' || hasRestoredScroll.current || items.length === 0) return;
    hasRestoredScroll.current = true;
    const saved = readSavedPosition(`${SCROLL_STORAGE_PREFIX}${scrollKey}`);
    if (saved > 0) {
      requestAnimationFrame(() => gridReference.current?.scrollTo({ scrollTop: saved }));
    }
  }, [scrollKey, navigationType, items.length]);

  if (isLoading && items.length === 0) {
    return (
      <LoadingContainer sx={{ height: '100%' }}>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  if (!isLoading && items.length === 0 && !hasNextPage && total === 0) {
    return <>{emptyState}</>;
  }

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <AutoSizer>
        {size => {
          const { height, width } = size;
          const usableWidth = width - PADDING * 2;
          const columnCount = Math.max(1, Math.floor((usableWidth + GAP) / (MIN_CARD_WIDTH + GAP)));
          columnCountReference.current = columnCount;
          const columnWidth = (usableWidth - GAP * (columnCount - 1)) / columnCount + GAP;
          const totalCells = getRenderableCellCount({
            loadedCount: items.length,
            total,
            columnCount,
            hasNextPage,
          });
          const rowCount = Math.ceil(totalCells / columnCount);

          return (
            <Grid
              ref={gridReference}
              columnCount={columnCount}
              columnWidth={columnWidth}
              height={height}
              rowCount={rowCount}
              rowHeight={ROW_HEIGHT}
              width={width}
              overscanRowCount={OVERSCAN_ROW_COUNT}
              onScroll={handleScroll}
              onItemsRendered={handleItemsRendered}
              innerElementType={GridInner}
              style={{ overflowX: 'hidden' }}
            >
              {({ columnIndex, rowIndex, style }) => {
                const index = rowIndex * columnCount + columnIndex;
                const adjustedStyle = {
                  ...style,
                  left: Number(style.left) + PADDING,
                  top: Number(style.top) + PADDING,
                  width: Number(style.width) - GAP,
                  height: Number(style.height) - GAP,
                };

                if (index >= totalCells) {
                  return <div style={adjustedStyle} />;
                }

                if (index >= items.length) {
                  return (
                    <div style={adjustedStyle}>
                      <PlaceholderCard />
                    </div>
                  );
                }

                const item = items[index];
                return (
                  <div style={adjustedStyle} key={getItemKey(item)}>
                    {renderCard(item)}
                  </div>
                );
              }}
            </Grid>
          );
        }}
      </AutoSizer>
    </Box>
  );
};

export const CardGrid = memo(CardGridInner) as typeof CardGridInner;

// Reloading the pages a previous visit scrolled through only pays for itself if there is an offset
// to scroll back to. Landing at the top costs a request per page and shows the first row regardless.
export const getSavedCardPageCount = (scrollKey: string): number => {
  const savedScroll = readSavedPosition(`${SCROLL_STORAGE_PREFIX}${scrollKey}`);
  return savedScroll > 0 ? readSavedPosition(`${PAGE_COUNT_STORAGE_PREFIX}${scrollKey}`) : 0;
};
